import {FC, lazy, useCallback, useEffect, useMemo, useState} from 'react';
import {LeadFormDataType} from 'api/types/LeadForm';
import {VIEW} from 'utils/types';
import useLeadFormController from 'utils/hooks/useLeadFormController';
import useAnalytics from 'utils/hooks/useAnalytics';
import {useLeadFormNavigation} from 'utils/hooks/useNavigation';
import usePhoneVerificationController from 'utils/hooks/usePhoneVerification';
import {useSetBarColor} from 'utils/hooks/useSetBarColor';
import {
    ContactField,
    FIXED_SCREENS,
    LeadForm,
    LeadFormDataType as LeadFormDataTypeView,
} from '@target-web/leadads-form-view';
import {getVKAwayLink, hasClientInUrl, hasVkAppId, isOKClient, isSdkClient, isVkClient} from 'utils/utils';
import {CODE_LENGTH} from 'utils/constants';
import {bridge, onCopyToClickboard} from 'utils/bridge';
import {AppProps} from './types';

const HlsPlayer = lazy(() => import(/* webpackChunkName: "hls" */ './HlsPlayer/HlcPlayer'));

type AppLeadFormProps = AppProps & {
    formData: LeadFormDataType;
    formDataHash: string | null;
};
const AppLeadForm: FC<AppLeadFormProps> = ({formData, formDataHash, layout, isShowCloseBtn}) => {
    const [filledContactFields, setFilledContactFields] = useState<ContactField[]>([]);
    const formController = useLeadFormController({leadForm: formData});
    const phoneVerificationController = usePhoneVerificationController(CODE_LENGTH);
    const isShowSocialMediaProfile = useMemo(() => {
        return (
            isOKClient() ||
            isVkClient() ||
            (!hasClientInUrl() && hasVkAppId()) || // VK
            // TRG-188166: Временное решение. Для SDK нужен доп ключ, чтобы уже на этапе инициализации
            // без получения данных пользователя узнать о нужно ли отрисовывать поле или нет
            (isSdkClient() && !!(formController.userInfo.general?.vk_id || formController.userInfo.general?.ok_id))
        );
    }, [formController]);
    const {clickToCallAnalyticEvent, clickToExternalLinkAnalyticEvent, contactInfoFillEvent} = useAnalytics();
    const {history, goBack, goNext, closeForm, showAgreement, onTransition, isLoading} = useLeadFormNavigation({
        formData,
        formController,
        phoneVerificationController,
        canSendSocialMediaProfile: isShowSocialMediaProfile,
    });

    useSetBarColor();

    const newFilledContacts = useMemo(
        () =>
            formData.contact_fields
                .filter((field) => formController?.formState.contactInfo[field]?.value)
                .filter((field) => !filledContactFields.includes(field)),
        [filledContactFields, formController?.formState.contactInfo, formData.contact_fields],
    );

    useEffect(() => {
        if (newFilledContacts.length && history[history.length - 1] === FIXED_SCREENS.ONE_PAGE) {
            setFilledContactFields((prev) => [...prev, ...newFilledContacts]);
            newFilledContacts.forEach((field) => contactInfoFillEvent(field));
        }
    }, [contactInfoFillEvent, history, newFilledContacts]);

    const goBackWithHooks = useCallback(() => {
        const curPage = history[history.length - 1];
        if (curPage === FIXED_SCREENS.PHONE_VERIFICATION) {
            phoneVerificationController.changePhone();
        }

        goBack();
    }, [goBack, history, phoneVerificationController]);

    const leadFormViewData = {
        ...formData,
        ...(formData.result_info
            ? {
                  result_info: {
                      ...formData.result_info,
                      ...(formData.result_info.site_url
                          ? {
                                site_url:
                                    layout === VIEW.miniapp // миниапы сами подставляют свой away, а на десктопы уже мы
                                        ? formData.result_info.site_url
                                        : getVKAwayLink(formData.result_info.site_url),
                            }
                          : {}),
                  },
              }
            : {}),
    } satisfies LeadFormDataTypeView;

    return (
        <LeadForm
            activePanelId={history[history.length - 1]}
            clickToCall={clickToCallAnalyticEvent}
            clickToExternalLink={clickToExternalLinkAnalyticEvent}
            formController={formController}
            phoneVerificationController={phoneVerificationController}
            goBack={goBackWithHooks}
            goNext={goNext}
            closeForm={closeForm}
            copyToClickboard={onCopyToClickboard}
            showAgreement={showAgreement}
            history={history}
            layout={layout}
            leadForm={leadFormViewData}
            isShowCloseBtn={isShowCloseBtn}
            isShowSocialMediaProfile={isShowSocialMediaProfile}
            onTransition={onTransition}
            isBridgeAvailable={!!bridge}
            leadFormDataHash={formDataHash}
            isLoading={isLoading}
            HlsComponent={HlsPlayer}
        />
    );
};

export default AppLeadForm;
